import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../../components/ButtonComponent';
import '../../../styles/widgets/clock/runJump.scss';
import globalStore from '../../../store';
import InputBox from '../../../components/InputBox';
import { announce } from '../../../helper/announcer';
var RunJump = forwardRef(function (props, ref) {
    var onChange = props.onChange, currentHour = props.currentHour, currentMinute = props.currentMinute, currentPeriod = props.currentPeriod, initialTab = props.initialTab, initialJumpValue = props.initialJumpValue;
    var t = useTranslation().t;
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var _a = useState(initialTab || 'run'), toggleSelected = _a[0], setToggleSelected = _a[1];
    // Track previous tab for tabChange event
    var previousTabRef = useRef(initialTab || 'run');
    // Track if we're syncing from props (to prevent tabChange event on undo/redo)
    var isSyncingFromPropsRef = useRef(false);
    // Track last known initialTab prop to detect actual prop changes
    var lastInitialTabRef = useRef(initialTab);
    var _b = useState(initialJumpValue || '1'), jumpBy = _b[0], setJumpBy = _b[1];
    // Track last known initialJumpValue prop to detect actual prop changes (undo/redo restore)
    var lastInitialJumpValueRef = useRef(initialJumpValue);
    var _c = useState(false), isRunning = _c[0], setIsRunning = _c[1];
    var _d = useState(''), runJumpAnnouncement = _d[0], setRunJumpAnnouncement = _d[1];
    var _e = useState(false), isInsidePanel = _e[0], setIsInsidePanel = _e[1];
    var wrapperRef = useRef(null);
    // Runtime state - internal to RunJump
    var runtimeSecondsRef = useRef(0);
    var intervalRef = useRef(null);
    // Store the base time when run starts/resumes
    var baseTimeRef = useRef({ hour: currentHour, minute: currentMinute, period: currentPeriod });
    useEffect(function () {
        onChange && onChange('toggleRun', { id: 'toggleRun', runActive: toggleSelected === 'run' });
        // Only stop timer on user-driven tab changes, not prop-driven (undo/redo)
        // During undo/redo, the timer is already stopped and seconds should be preserved
        if (!isSyncingFromPropsRef.current) {
            handleRunStop();
        }
        // Fire tabChange event only if:
        // 1. Tab actually changed (not on initial mount)
        // 2. Not a prop-driven sync (undo/redo)
        if (previousTabRef.current !== toggleSelected && !isSyncingFromPropsRef.current) {
            onChange &&
                onChange('tabChange', {
                    id: 'tabChange',
                    tab: toggleSelected,
                    previousTab: previousTabRef.current,
                });
            var mode_1 = toggleSelected === 'run' ? t('text.runMode') : t('text.jumpMode');
            setRunJumpAnnouncement('');
            requestAnimationFrame(function () {
                return setRunJumpAnnouncement(t('text.timerModeAnnouncement', { mode: mode_1 }));
            });
        }
        previousTabRef.current = toggleSelected;
        isSyncingFromPropsRef.current = false; // Reset flag after processing
    }, [toggleSelected]);
    // Sync tab state when initialTab prop changes (e.g., from undo/redo)
    useEffect(function () {
        // Treat undefined as 'run' (default value)
        var effectiveInitialTab = initialTab !== null && initialTab !== void 0 ? initialTab : 'run';
        // Check if the prop actually changed (compare actual values, including undefined)
        var propChanged = initialTab !== lastInitialTabRef.current;
        if (propChanged) {
            lastInitialTabRef.current = initialTab;
            if (effectiveInitialTab !== toggleSelected) {
                isSyncingFromPropsRef.current = true; // Signal this is a prop-driven change
                setToggleSelected(effectiveInitialTab);
            }
        }
    }, [initialTab, toggleSelected]);
    // Sync jump value when initialJumpValue prop changes (e.g., from undo/redo)
    useEffect(function () {
        var effectiveInitialJumpValue = initialJumpValue !== null && initialJumpValue !== void 0 ? initialJumpValue : '1';
        var propChanged = initialJumpValue !== lastInitialJumpValueRef.current;
        if (propChanged) {
            lastInitialJumpValueRef.current = initialJumpValue;
            if (effectiveInitialJumpValue !== jumpBy) {
                setJumpBy(effectiveInitialJumpValue);
            }
        }
    }, [initialJumpValue, jumpBy]);
    // Update base time when external time changes (e.g., from jump or manual adjustment)
    // Only update if not running AND at a fresh start (no accumulated seconds)
    // This prevents stale props from overwriting the base time when pausing
    useEffect(function () {
        if (!isRunning && runtimeSecondsRef.current === 0) {
            baseTimeRef.current = { hour: currentHour, minute: currentMinute, period: currentPeriod };
        }
    }, [currentHour, currentMinute, currentPeriod, isRunning]);
    // Cleanup interval on unmount
    useEffect(function () {
        return function () {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);
    var startTimer = function () {
        // Prevent multiple intervals
        if (intervalRef.current) {
            return;
        }
        // Store current time as base only if starting fresh (not resuming)
        if (runtimeSecondsRef.current === 0) {
            baseTimeRef.current = { hour: currentHour, minute: currentMinute, period: currentPeriod };
        }
        // Don't reset runtimeSecondsRef.current here - keep previous value for resume
        intervalRef.current = setInterval(function () {
            runtimeSecondsRef.current += 1;
            var elapsedSeconds = runtimeSecondsRef.current;
            // Calculate fractional minutes elapsed
            var minuteIncrement = elapsedSeconds / 60;
            // Calculate new minute (fractional for smooth hand movement)
            // NOTE: Do NOT add hourIncrement to hour - the angle calculation already
            // accounts for minute position in the hour hand angle. Adding hourIncrement
            // would double-count and cause premature hour display changes.
            var newMinute = baseTimeRef.current.minute + minuteIncrement;
            var newHour = baseTimeRef.current.hour;
            var newPeriod = baseTimeRef.current.period;
            // Handle minute rollover - only increment hour when minutes >= 60
            while (newMinute >= 60) {
                newMinute -= 60;
                newHour += 1;
                // Handle period change when crossing 12
                if (newHour === 12) {
                    newPeriod = newPeriod === 'AM' ? 'PM' : 'AM';
                }
                // Handle hour rollover (12-hour format: 12 -> 1)
                if (newHour > 12) {
                    newHour = 1;
                }
            }
            // Send update to parent
            onChange('timeUpdate', {
                id: 'timeUpdate',
                hour: newHour,
                minute: newMinute,
                period: newPeriod,
            });
            onChange('secondsUpdate', { id: 'secondsUpdate', seconds: runtimeSecondsRef.current });
        }, 1000); // Tick every 1 second
    };
    var pauseTimer = function () {
        // Only clear interval, don't reset seconds (for pause functionality)
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };
    var handleRunPlay = function () {
        if (isRunning) {
            // Pause (don't reset seconds)
            pauseTimer();
            setIsRunning(false);
            onChange('runAction', { id: 'runAction', action: 'pause' });
            announce(t('text.timerPaused'));
        }
        else {
            // Start/Resume
            startTimer();
            setIsRunning(true);
            onChange('runAction', { id: 'runAction', action: 'play' });
            announce(t('text.timerStarted'));
        }
    };
    var handleRunStop = function () {
        // Clear interval but do NOT reset runtimeSeconds yet
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsRunning(false);
        // Fire runAction BEFORE resetting runtimeSeconds so parent can capture the value
        onChange('runAction', { id: 'runAction', action: 'stop' });
        // Now reset runtimeSeconds and notify parent
        runtimeSecondsRef.current = 0;
        onChange('secondsUpdate', { id: 'secondsUpdate', seconds: 0 });
        // announce(t('text.timerStopped'));
    };
    // Expose methods to parent via ref
    useImperativeHandle(ref, function () { return ({
        handleRunStop: handleRunStop,
        handleRunPlay: handleRunPlay,
        getIsRunning: function () { return isRunning; },
        getRuntimeSeconds: function () { return runtimeSecondsRef.current; },
        setRuntimeSeconds: function (seconds) {
            runtimeSecondsRef.current = seconds;
            // Update second hand display only
            // NOTE: Do NOT reset baseTimeRef here. baseTimeRef is set in startTimer() when runtimeSeconds===0
            // (fresh start). Resetting it here to current (possibly fractional) props would corrupt the base
            // time and cause the clock to jump by ~1 minute on every pause/play cycle after the first minute.
            onChange('secondsUpdate', { id: 'secondsUpdate', seconds: seconds });
        },
    }); });
    var handleWrapperKeyDown = function (e) {
        var _a, _b;
        // Stop arrow keys from bubbling to clock
        if (e.key.startsWith('Arrow')) {
            e.stopPropagation();
        }
        if (!isInsidePanel) {
            // On wrapper level: Enter/Space enters the panel
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsInsidePanel(true);
                setTimeout(function () {
                    var _a;
                    var first = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]):not([tabindex="-1"]), input:not([disabled])');
                    first === null || first === void 0 ? void 0 : first.focus();
                }, 50);
            }
            return;
        }
        // Inside panel
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setIsInsidePanel(false);
            (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        if (e.key === 'Tab') {
            var focusable = (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), input:not([disabled])');
            if (!focusable || focusable.length === 0)
                return;
            var first = focusable[0];
            var last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };
    return (_jsxs("div", { className: "runJumpWrapper", ref: wrapperRef, tabIndex: 0, role: "group", "aria-label": t('text.runAndJumpPanel'), onKeyDown: handleWrapperKeyDown, children: [_jsxs("div", { className: "runJumpToggle", role: "group", "aria-label": t('text.toggleButton'), children: [_jsx(ButtonComponent, { text: 'Run', defaultCSS: "toggle", normalCSS: '', clickHandler: function () {
                            setToggleSelected('run');
                        }, ariaLabel: "text.runMode", ariaPressed: toggleSelected === 'run', isActive: toggleSelected === 'run', clickActive: true, controlActiveFromOutside: true, avoidDefaultStyle: true, type: "textType3", tabIndex: isInsidePanel ? 0 : -1 }), _jsx(ButtonComponent, { text: 'Jump', defaultCSS: "toggle", normalCSS: '', clickHandler: function () {
                            setToggleSelected('jump');
                        }, ariaLabel: "text.jumpMode", ariaPressed: toggleSelected === 'jump', isActive: toggleSelected === 'jump', clickActive: true, controlActiveFromOutside: true, avoidDefaultStyle: true, type: "textType3", tabIndex: isInsidePanel ? 0 : -1 })] }), _jsxs("div", { className: "runJumpContent", "aria-label": toggleSelected === 'run' ? t('text.runControls') : t('text.jumpControls'), children: [toggleSelected === 'jump' && (_jsxs("div", { className: "jumpWrapper", children: [_jsx(ButtonComponent, { imgWidth: 12.5, imgSrc: btnConfig.leftArrow.imgSrc, clickHandler: function () {
                                    onChange('jump', { id: 'jump', jumpBy: jumpBy, addBy: false });
                                    setRunJumpAnnouncement('');
                                    requestAnimationFrame(function () {
                                        return setRunJumpAnnouncement(t('text.jumpedBackward', { minutes: jumpBy }));
                                    });
                                }, defaultCSS: "arrow left", ariaLabel: t('text.jumpBackward'), type: "imageType1", tabIndex: isInsidePanel ? 0 : -1 }), _jsx(InputBox, { id: 'jumpInput', defaultCSS: "value", isNumeric: true, type: "text", value: jumpBy, maxLength: 2, min: 1, max: 99, ariaLabel: "".concat(t('text.jumpValueInput'), ", ").concat(jumpBy), 
                                // onInput={(_id, data) => {
                                //   setMinutes(+data);
                                // }}
                                onChange: function (_id, event, data) {
                                    if (event === 'blur' && data !== jumpBy) {
                                        onChange('jumpValueChange', {
                                            id: 'jumpValueChange',
                                            jumpBy: data,
                                            previousJumpBy: jumpBy,
                                        });
                                        setJumpBy(data);
                                    }
                                } }), _jsx(ButtonComponent, { imgWidth: 12.5, clickHandler: function () {
                                    onChange('jump', { id: 'jump', jumpBy: jumpBy, addBy: true });
                                    setRunJumpAnnouncement('');
                                    requestAnimationFrame(function () {
                                        return setRunJumpAnnouncement(t('text.jumpedForward', { minutes: jumpBy }));
                                    });
                                }, defaultCSS: "arrow right", imgSrc: btnConfig.rightArrow.imgSrc, ariaLabel: t('text.jumpForward'), type: "imageType1", tabIndex: isInsidePanel ? 0 : -1 })] })), toggleSelected === 'run' && (_jsxs("div", { className: "runWrapper", children: [_jsx(ButtonComponent, { clickHandler: handleRunStop, defaultCSS: "stop", imgSrc: btnConfig.runStop.imgSrc, ariaLabel: "text.stopTimer", type: "imageType6", disabled: !isRunning && runtimeSecondsRef.current === 0, tabIndex: !isRunning && runtimeSecondsRef.current === 0 ? -1 : isInsidePanel ? 0 : -1 }, 'runStop'), _jsx(ButtonComponent, { clickHandler: handleRunPlay, defaultCSS: "play", imgSrc: btnConfig.runPlay.imgSrc, ariaLabel: isRunning ? 'text.pauseTimer' : 'text.playTimer', type: "imageType6", isActive: isRunning, clickActive: true, controlActiveFromOutside: true, tabIndex: isInsidePanel ? 0 : -1 }, 'runPlay')] }))] }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", children: runJumpAnnouncement })] }));
});
export default RunJump;
